// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useEffect, useMemo, useState } from 'react';
import { useGlobalSearchParams, usePathname, useRouter } from 'expo-router';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { getCurrentTourId } from '@/services/currentTour';
import { getActiveClaimSessionSync, loadClaimSession, type ActiveClaimSession } from '@/services/claimSession';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';

export function BottomMenu() {
  const router = useRouter();
  const pathname = usePathname();
  const safePathname = pathname ?? '';
  const { tourId: tourParam, debug: debugParam } = useGlobalSearchParams<{ tourId?: string; debug?: string }>();
  const roundId = useRoundState((state) => state.roundId);
  const holeNumber = useRoundState((state) => state.hole);
  const scorerOnlyMode = useRoundState((state) => state.scorerOnlyMode);
  const requestHoleMenuOpen = useRoundState((state) => state.requestHoleMenuOpen);
  const [claimSession, setClaimSession] = useState<ActiveClaimSession | null>(() => getActiveClaimSessionSync());
  const { t } = useI18n();

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const roundIdFromPath = useMemo(() => {
    const parts = safePathname.split('/').filter(Boolean);
    const idx = parts.indexOf('round');
    if (idx < 0) return undefined;
    return parts[idx + 1];
  }, [safePathname]);
  const holeFromPath = useMemo(() => {
    const parts = safePathname.split('/').filter(Boolean);
    const idx = parts.indexOf('hole');
    if (idx < 0) return null;
    const raw = parts[idx + 1];
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [safePathname]);
  const activeRoundId = roundId ?? roundIdFromPath;
  const activeHoleNumber = holeNumber ?? holeFromPath;
  const hasRoundHoleContext = !!activeRoundId && !!activeHoleNumber;
  const hasClaimNavigationAccess = !!claimSession?.claimAccessToken;
  const disableTourNavigation = scorerOnlyMode && !hasClaimNavigationAccess;
  const disableRoundNavigation = (!hasRoundHoleContext) || (scorerOnlyMode && !hasClaimNavigationAccess);
  const isHoleRoute = /\/round\/[^/]+\/hole\//.test(safePathname);
  const handleTourPress = () => {
    if (disableTourNavigation) return;
    const tourId = getCurrentTourId() ?? claimSession?.tourId ?? null;
    if (tourId) {
      router.push(`/tour/${tourId}/leaderboard`);
    } else {
      router.push('/tournaments');
    }
  };

  const handleHolePress = () => {
    if (!hasRoundHoleContext) {
      return;
    }
    if (isHoleRoute) {
      requestHoleMenuOpen();
      return;
    }
    const params: string[] = [];
    if (debugParam === 'true') params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    if (activeRoundId && activeHoleNumber) {
      const nextHole = activeHoleNumber;
      router.push(`/round/${activeRoundId}/hole/${nextHole}/compact${query}`);
      return;
    }
  };

  const handleScorecardPress = () => {
    if (activeRoundId && hasRoundHoleContext) {
      router.push(`/round/${activeRoundId}/scorecard`);
      return;
    }
  };

  const handleRoundResultsPress = () => {
    if (disableRoundNavigation) return;
    if (activeRoundId && hasRoundHoleContext) {
      const parts: string[] = [];
      if (tourParam) parts.push(`tourId=${tourParam}`);
      if (debugParam === 'true') parts.push('debug=true');
      const query = parts.length ? `?${parts.join('&')}` : '';
      router.push(`/round/${activeRoundId}/summary${query}`);
      return;
    }
  };

  const isTourActive = safePathname.startsWith('/tour') || safePathname.startsWith('/tournaments') || safePathname.startsWith('/my-tours');
  const isHoleActive = safePathname.includes('/hole/');
  const isRoundActive = safePathname.includes('/summary') || safePathname.includes('/select-games') || safePathname.includes('/roundstart');
  const isScorecardActive = safePathname.includes('/scorecard');

  return (
    <View style={styles.container}>
      <Pressable
        style={[styles.item, isHoleActive && styles.itemActive, !hasRoundHoleContext && styles.itemDisabled]}
        onPress={handleHolePress}
        disabled={!hasRoundHoleContext}
      >
        <FontAwesome name="flag-checkered" size={18} color={hasRoundHoleContext ? (isHoleActive ? appColors.accent.primary : appColors.text.secondary) : appColors.text.muted} />
        <Text style={[styles.label, isHoleActive && styles.labelActive, !hasRoundHoleContext && styles.labelDisabled]}>{t('menu.hole')}</Text>
      </Pressable>
      <Pressable
        style={[styles.item, isTourActive && !disableTourNavigation && styles.itemActive, disableTourNavigation && styles.itemDisabled]}
        onPress={handleTourPress}
        disabled={disableTourNavigation}
      >
        <FontAwesome
          name="trophy"
          size={18}
          color={disableTourNavigation ? appColors.text.muted : (isTourActive ? appColors.accent.primary : appColors.text.secondary)}
        />
        <Text style={[styles.label, isTourActive && !disableTourNavigation && styles.labelActive, disableTourNavigation && styles.labelDisabled]}>
          {t('menu.tour')}
        </Text>
      </Pressable>
      <Pressable
        style={[styles.item, isRoundActive && !disableRoundNavigation && styles.itemActive, disableRoundNavigation && styles.itemDisabled]}
        onPress={handleRoundResultsPress}
        disabled={disableRoundNavigation}
      >
        <FontAwesome
          name="list-alt"
          size={18}
          color={hasRoundHoleContext && !disableRoundNavigation
            ? (isRoundActive ? appColors.accent.primary : appColors.text.secondary)
            : appColors.text.muted}
        />
        <Text style={[styles.label, isRoundActive && !disableRoundNavigation && styles.labelActive, disableRoundNavigation && styles.labelDisabled]}>
          {t('menu.roundResults')}
        </Text>
      </Pressable>
      <Pressable
        style={[styles.item, isScorecardActive && styles.itemActive, !hasRoundHoleContext && styles.itemDisabled]}
        onPress={handleScorecardPress}
        disabled={!hasRoundHoleContext}
      >
        <FontAwesome name="th" size={18} color={hasRoundHoleContext ? (isScorecardActive ? appColors.accent.primary : appColors.text.secondary) : appColors.text.muted} />
        <Text style={[styles.label, isScorecardActive && styles.labelActive, !hasRoundHoleContext && styles.labelDisabled]}>{t('menu.scorecard')}</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 62,
    backgroundColor: 'rgba(1, 24, 6, 0.8)',
    borderTopWidth: 1,
    borderTopColor: appColors.border.default,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: 10,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      },
    }),
  },
  item: {
    minWidth: 70,
    minHeight: 42,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  itemActive: {
    backgroundColor: appColors.accent.subtleBg,
  },
  itemDisabled: {
    opacity: 0.75,
  },
  label: {
    color: appColors.text.secondary,
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  labelActive: {
    color: appColors.accent.primary,
    fontWeight: '700',
  },
  labelDisabled: {
    color: appColors.text.muted,
  },
});
